import React, { useEffect, useState, useRef, useCallback, useMemo, useImperativeHandle, forwardRef } from 'react';
import { supabase } from '../supabase';
import PostCard from './PostCard';
import { fetchBiomarkerAttachments } from '../utils/fetchBiomarkerAttachments';
import { dedupeRequest, getCacheKey } from '../utils/queryDeduplication';
import { sanitizeErrorForLog } from '../utils/sanitizeErrorForLog';
import { useProtocolActions } from '../hooks/useProtocolActions';
import './PostFeed.css';

const PAGE_SIZE = 10;

// Re-export for any code that imports from PostFeed
export { fetchBiomarkerAttachments };

// Helper: Extract all biohack IDs and categories from a post
const extractBiohacksFromPost = (post) => {
  const hackIds = new Set();
  const categories = new Set();
  
  // Direct biohack tags
  (post.post_biohack_tags || []).forEach(tag => {
    if (tag.biohacks?.id) {
      hackIds.add(tag.biohacks.id);
      if (tag.biohacks.category) categories.add(tag.biohacks.category);
    }
  });
  
  // Biohacks from protocol attachments
  (post.post_attachments || [])
    .filter(att => att.type === 'protocols' && att.post_attachment_protocols)
    .flatMap(att => att.post_attachment_protocols || [])
    .flatMap(pap => pap.protocols?.protocol_hacks || [])
    .forEach(ph => {
      if (ph.biohacks?.id) {
        hackIds.add(ph.biohacks.id);
        if (ph.biohacks.category) categories.add(ph.biohacks.category);
      }
    });
  
  return { hackIds, categories };
};

// Helper: Extract all biomarker IDs and categories from a post
const extractBiomarkersFromPost = (post) => {
  const biomarkerIds = new Set();
  const categories = new Set();
  
  (post.post_biomarker_tags || []).forEach(tag => {
    if (tag.biomarkers?.id) {
      biomarkerIds.add(tag.biomarkers.id);
      if (tag.biomarkers.category) categories.add(tag.biomarkers.category);
    }
  });
  
  return { biomarkerIds, categories };
};

// Helper: Check if sets have any intersection
const hasIntersection = (set1, set2) => {
  if (!set1 || !set2 || set1.size === 0 || set2.size === 0) return false;
  for (const item of set1) {
    if (set2.has(item)) return true;
  }
  return false;
};

// Helper: Normalize ID to string for safe comparison
const normalizeId = (id) => String(id);

// Helper: Filter posts based on selected filters
const filterPosts = (posts, selectedHacks, selectedCategories, selectedBiomarkers, selectedBiomarkerCategories) => {
  // Early exit if no filters
  const hasFilters = 
    (selectedHacks?.length > 0) || 
    (selectedCategories?.length > 0) || 
    (selectedBiomarkers?.length > 0) || 
    (selectedBiomarkerCategories?.length > 0);
  
  if (!hasFilters) return posts;
  
  // Pre-compute filter sets - normalize IDs to strings
  const selectedHacksSet = selectedHacks?.length > 0 ? new Set(selectedHacks.map(normalizeId)) : null;
  const selectedCategoriesSet = selectedCategories?.length > 0 ? new Set(selectedCategories) : null;
  const selectedBiomarkersSet = selectedBiomarkers?.length > 0 ? new Set(selectedBiomarkers.map(normalizeId)) : null;
  const selectedBiomarkerCategoriesSet = selectedBiomarkerCategories?.length > 0 ? new Set(selectedBiomarkerCategories) : null;
  
  const filtered = posts.filter(post => {
    // Check biohack filters
    let biohackMatch = true;
    if (selectedHacksSet || selectedCategoriesSet) {
      const { hackIds, categories } = extractBiohacksFromPost(post);
      // Normalize extracted IDs
      const normalizedHackIds = new Set(Array.from(hackIds).map(normalizeId));
      biohackMatch = hasIntersection(selectedHacksSet, normalizedHackIds) || hasIntersection(selectedCategoriesSet, categories);
    }
    
    // Check biomarker filters
    let biomarkerMatch = true;
    if (selectedBiomarkersSet || selectedBiomarkerCategoriesSet) {
      const { biomarkerIds, categories } = extractBiomarkersFromPost(post);
      // Normalize extracted IDs
      const normalizedBiomarkerIds = new Set(Array.from(biomarkerIds).map(normalizeId));
      biomarkerMatch = hasIntersection(selectedBiomarkersSet, normalizedBiomarkerIds) || hasIntersection(selectedBiomarkerCategoriesSet, categories);
    }
    
    // Post must match both groups (AND logic)
    return biohackMatch && biomarkerMatch;
  });

  console.log('PostFeed filtering:', {
    totalPosts: posts.length,
    filteredPosts: filtered.length,
    filters: {
      hacks: selectedHacks,
      categories: selectedCategories,
      biomarkers: selectedBiomarkers,
      bmCategories: selectedBiomarkerCategories
    }
  });

  return filtered;
};

const fetchPosts = async (page, selectedHacks, selectedCategories, selectedBiomarkers, selectedBiomarkerCategories, activeFilter) => {
  // Create cache key based on all parameters
  const filtersKey = [
    page,
    activeFilter,
    (selectedHacks || []).sort().join(','),
    (selectedCategories || []).sort().join(','),
    (selectedBiomarkers || []).sort().join(','),
    (selectedBiomarkerCategories || []).sort().join(',')
  ].join('|');
  const cacheKey = getCacheKey('fetchPosts', filtersKey);
  
  return dedupeRequest(cacheKey, async () => {
    // Get current user session for following filter
    const { data: { session } } = await supabase.auth.getSession();
    const currentUserId = session?.user?.id;
    
    // If filtering by following, get list of user IDs the current user follows
    // OPTIMIZATION: Cache follows list separately (not per-page) to avoid refetching on pagination
    let followingUserIds = null;
    if (activeFilter === 'following') {
      if (!currentUserId) {
        // User not logged in, return empty results for following filter
        return { data: [], error: null, originalDataLength: 0 };
      }
      
      // Fetch users the current user follows with separate caching
      // This cache is shared across all pages, so we only fetch once per session
      const followsCacheKey = getCacheKey('fetchFollowingUserIds', currentUserId);
      const followsData = await dedupeRequest(followsCacheKey, async () => {
        const { data, error } = await supabase
          .from('follows')
          .select('followee_id')
          .eq('follower_id', currentUserId);
        
        if (error) {
          console.error('Error fetching follows:', sanitizeErrorForLog(error));
          throw error;
        }
        
        return data;
      });
      
      followingUserIds = (followsData || []).map(f => f.followee_id);
      
      // If user doesn't follow anyone, return empty results
      if (followingUserIds.length === 0) {
        return { data: [], error: null, originalDataLength: 0 };
      }
    }
    
    // OPTIMIZED FOR EGRESS: Reduced data fetched to minimize Supabase cache size:
    // 1. Replaced * with only needed post columns
    // 2. Include protocol_ratings so ProtocolShield shows consistent pop scores (likes - dislikes) on feed as on protocols/posts tabs
    // 3. Only fetch essential fields from nested relationships
    // Note: Visibility filtering is handled by RLS policies, which allow users to see:
    // - Public posts (for everyone)
    // - Their own posts (regardless of visibility)
    let query = supabase
    .from('posts')
    .select(`id, user_id, title, content, created_at, updated_at, visibility, is_deleted, shared_from, reposts_count, likes_count,
      user_profiles!posts_user_id_fkey(username, user_level, profile_picture_url, user_id, gender),
      post_attachments:post_attachments_post_id_fkey(
        id, type, sort_order,
        post_attachment_protocols:post_attachment_protocols_post_attachment_id_fkey(
          protocols:protocol_id(
            id, name, description, pop_score, adherence_score, created_at,
            protocol_hacks:protocol_hacks_protocol_id_fkey(
              id, position,
              biohacks:protocol_hacks_hack_id_fkey(id, name, image_url, category)
            ),
            protocol_ratings ( id, rating ),
            user_profiles!protocols_created_by_fkey(user_id, username)
          )
        ),
        post_attachment_images:post_attachment_images_post_attachment_id_fkey(
          id, image_url, file_name, mime_type
        ),
        post_attachment_reports:post_attachment_reports_post_attachment_id_fkey(
          protocol_report_snapshots:report_id(
            id, protocol_id, title, snapshot_date, created_at
          )
        )
      ),
      post_biohack_tags:post_biohack_tags_post_id_fkey(
        biohacks:biohack_id(id, name, image_url, category)
      ),
      post_biomarker_tags:post_biomarker_tags_post_id_fkey(
        biomarkers:biomarker_id(id, name, category, unit)
      )
    `)
    .eq('is_deleted', false)
    .lte('created_at', new Date().toISOString());

    // Apply following filter if needed
    if (activeFilter === 'following' && followingUserIds && followingUserIds.length > 0) {
      query = query.in('user_id', followingUserIds);
    }

  // Apply ordering based on filter
  switch (activeFilter) {
    case 'top':
      query = query.order('likes_count', { ascending: false, nullsLast: true });
      break;
    case 'following':
    case 'latest':
    default:
      query = query.order('created_at', { ascending: false });
      break;
  }

  query = query.range(page * PAGE_SIZE, (page + 1) * PAGE_SIZE - 1);

  const { data, error } = await query;
  
  if (error) {
    const is406Error = error.status === 406 || error.message?.includes('406') || error.code === '406';
    if (is406Error) {
      console.error('🚨 406 ERROR in fetchPosts:', sanitizeErrorForLog(error));
    } else {
      console.error('Supabase query error:', sanitizeErrorForLog(error));
    }
  }
  
    // Apply client-side filtering if needed
    const filteredData = data ? filterPosts(data, selectedHacks, selectedCategories, selectedBiomarkers, selectedBiomarkerCategories) : null;
    // Track original data length to determine if there are more posts in the database
    const originalDataLength = data ? data.length : 0;
    
    return { data: filteredData, error, originalDataLength };
  });
};

// Select shape for original (shared-from) posts - must match PostCard single fetch for consistency
const ORIGINAL_POST_SELECT = `
  id,
  user_id,
  title,
  content,
  created_at,
  user_profiles!posts_user_id_fkey(username, user_level, profile_picture_url, gender),
  post_attachments:post_attachments_post_id_fkey(
    id, type, sort_order,
    post_attachment_protocols:post_attachment_protocols_post_attachment_id_fkey(
      protocols:protocol_id(
        id, name, description, created_at, adherence_score, pop_score,
        protocol_hacks:protocol_hacks_protocol_id_fkey(
          id, position,
          biohacks:protocol_hacks_hack_id_fkey(id, name, image_url, category)
        ),
        protocol_ratings ( id, rating ),
        user_profiles!protocols_created_by_fkey(user_id, username)
      )
    ),
    post_attachment_images:post_attachment_images_post_attachment_id_fkey(
      id, image_url, file_name, mime_type
    ),
    post_attachment_reports:post_attachment_reports_post_attachment_id_fkey(
      protocol_report_snapshots:report_id(
        id, protocol_id, title, snapshot_date, created_at
      )
    )
  ),
  post_biohack_tags:post_biohack_tags_post_id_fkey(
    biohacks:biohack_id(id, name, image_url, category)
  ),
  post_biomarker_tags:post_biomarker_tags_post_id_fkey(
    biomarkers:biomarker_id(id, name, category, unit)
  )
`;

// Batch-fetch original posts by id (for reposts). Deduped; only fetches IDs not already in cache.
const fetchOriginalPostsBatch = async (ids) => {
  if (!ids || ids.length === 0) return [];
  const sorted = [...ids].filter(Boolean).sort();
  const cacheKey = getCacheKey('fetchOriginalPostsBatch', sorted.join(','));
  return dedupeRequest(cacheKey, async () => {
    const { data, error } = await supabase
      .from('posts')
      .select(ORIGINAL_POST_SELECT)
      .in('id', sorted);
    if (error) {
      console.error('Error fetching original posts batch:', sanitizeErrorForLog(error));
      return [];
    }
    return data || [];
  });
};

// Helper to fetch like counts, liked status, comment counts, and repost status for a batch of posts
const fetchLikesAndCommentsForPosts = async (postIds) => {
  if (!postIds.length) return { likeCounts: {}, likedMap: {}, commentCounts: {}, repostedMap: {} };
  
  // Deduplicate requests
  const cacheKey = getCacheKey('fetchLikesAndCommentsForPosts', postIds);
  
  return dedupeRequest(cacheKey, async () => {
    // Get current user - use getSession() instead of getUser() to avoid network requests
    const { data: { session } } = await supabase.auth.getSession();
    let userId = session?.user?.id;
    
    // Fetch all post_likes for these posts
    const { data: likeRows, error: likesError } = await supabase
    .from('post_likes')
    .select('post_id, user_id, id')
    .in('post_id', postIds);
  
  if (likesError) {
    const is406Error = likesError.status === 406 || likesError.message?.includes('406') || likesError.code === '406';
    if (is406Error) {
      console.error('🚨 406 ERROR in fetchLikesAndCommentsForPosts (likes query):', {
        error: likesError,
        postIds: postIds,
        timestamp: new Date().toISOString()
      });
    }
  }
  
  // Fetch all post_comments for these posts (excluding deleted ones)
  const { data: commentRows, error: commentsError } = await supabase
    .from('post_comments')
    .select('post_id')
    .in('post_id', postIds)
    .eq('is_deleted', false);
  
  if (commentsError) {
    const is406Error = commentsError.status === 406 || commentsError.message?.includes('406') || commentsError.code === '406';
    if (is406Error) {
      console.error('🚨 406 ERROR in fetchLikesAndCommentsForPosts (comments query):', {
        error: commentsError,
        postIds: postIds,
        timestamp: new Date().toISOString()
      });
    }
  }
  
  // Fetch all reposts by current user for these posts (batch query to avoid N individual requests)
  let repostedMap = {};
  if (userId) {
    const { data: repostRows, error: repostsError } = await supabase
      .from('posts')
      .select('id, shared_from')
      .in('shared_from', postIds)
      .eq('user_id', userId);
    
    if (repostsError) {
      const is406Error = repostsError.status === 406 || repostsError.message?.includes('406') || repostsError.code === '406';
      if (is406Error) {
        console.error('🚨 406 ERROR in fetchLikesAndCommentsForPosts (reposts query):', {
          error: repostsError,
          postIds: postIds,
          timestamp: new Date().toISOString()
        });
      }
    } else if (repostRows) {
      // Build map of original_post_id -> repost_post_id
      repostRows.forEach(repost => {
        if (repost.shared_from) {
          repostedMap[repost.shared_from] = repost.id;
        }
      });
    }
  }
  
  // Count likes per post
  let likeCounts = {};
  let likedMap = {};
  (likeRows || []).forEach(row => {
    likeCounts[row.post_id] = (likeCounts[row.post_id] || 0) + 1;
    if (userId && row.user_id === userId) {
      likedMap[row.post_id] = row.id;
    }
  });
    // Count comments per post
    let commentCounts = {};
    (commentRows || []).forEach(row => {
      commentCounts[row.post_id] = (commentCounts[row.post_id] || 0) + 1;
    });
    return { likeCounts, likedMap, commentCounts, repostedMap };
  });
};

// Batch-fetch follow status for multiple authors (current user following them or not)
const fetchFollowStatusesForAuthors = async (currentUserId, authorIds) => {
  if (!currentUserId || !authorIds.length) return {};
  const ids = [...new Set(authorIds)].filter((id) => id !== currentUserId);
  if (!ids.length) return {};
  const cacheKey = getCacheKey('fetchFollowStatusesForAuthors', [currentUserId, ...ids.sort()]);
  return dedupeRequest(cacheKey, async () => {
    const { data, error } = await supabase
      .from('follows')
      .select('followee_id, accepted_at')
      .eq('follower_id', currentUserId)
      .in('followee_id', ids);
    if (error) {
      console.error('Error fetching follow statuses:', sanitizeErrorForLog(error));
      return {};
    }
    const map = {};
    (data || []).forEach((row) => {
      map[row.followee_id] = {
        isFollowing: true,
        isPending: row.accepted_at === null,
      };
    });
    ids.forEach((id) => {
      if (!(id in map)) {
        map[id] = { isFollowing: false, isPending: false };
      }
    });
    return map;
  });
};

const PostFeed = forwardRef(({ selectedHacks, selectedCategories, selectedBiomarkers, selectedBiomarkerCategories, activeFilter }, ref) => {
  const [posts, setPosts] = useState([]);
  const [, setAttachments] = useState({}); // Kept for effect closures (unused; attachment data lives on post.post_attachments)
  const [page, setPage] = useState(0);
  const [loading, setLoading] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const loader = useRef();
  const loadingRef = useRef(false);
  const hasMoreRef = useRef(true);
  const isInitialMountRef = useRef(true);
  const loadPostsInProgressRef = useRef(false); // Prevent duplicate requests
  const [likesInfo, setLikesInfo] = useState({}); // { [postId]: { count, liked, likeId, comments } }
  // Map of author (followee) id -> { isFollowing, isPending }; batched so PostCard doesn't fetch per post
  const [followStatusByAuthor, setFollowStatusByAuthor] = useState({});
  // Map of original post id (shared_from) -> full post; batched so PostCard doesn't fetch per repost
  const [originalPostsMap, setOriginalPostsMap] = useState({});
  const originalPostsMapRef = useRef({});
  
  // Fetch user protocol ratings once at the PostFeed level to avoid duplicate requests
  const { userVotes, handleVote: handleVoteAction, fetchUserVotes } = useProtocolActions();
  
  useEffect(() => {
    // Fetch user votes once when PostFeed mounts
    fetchUserVotes();
  }, [fetchUserVotes]);
  
  // Store filter values in refs to avoid dependency issues
  const filtersRef = useRef({ selectedHacks, selectedCategories, selectedBiomarkers, selectedBiomarkerCategories, activeFilter });
  
  useEffect(() => {
    filtersRef.current = { selectedHacks, selectedCategories, selectedBiomarkers, selectedBiomarkerCategories, activeFilter };
  }, [selectedHacks, selectedCategories, selectedBiomarkers, selectedBiomarkerCategories, activeFilter]);

  useEffect(() => {
    originalPostsMapRef.current = originalPostsMap;
  }, [originalPostsMap]);
  
  // Keep refs in sync with state
  useEffect(() => {
    loadingRef.current = loading;
  }, [loading]);
  
  useEffect(() => {
    hasMoreRef.current = hasMore;
  }, [hasMore]);

  // Helper to refresh like/comment counts for a single post
  // Note: This should be used sparingly - real-time subscriptions handle most updates
  const refreshCountsForPost = useCallback(async (postId) => {
    // Fetch like and comment info for just this post
    const { likeCounts, likedMap, commentCounts, repostedMap } = await fetchLikesAndCommentsForPosts([postId]);
    setLikesInfo((prev) => ({
      ...prev,
      [postId]: {
        count: likeCounts[postId] || 0,
        liked: !!likedMap[postId],
        likeId: likedMap[postId] || null,
        comments: commentCounts[postId] || 0,
        reposted: !!repostedMap[postId],
        repostId: repostedMap[postId] || null,
      },
    }));
    // Update the post in setPosts to force a re-render
    setPosts((prev) => prev.map(p => p.id === postId ? { ...p, likes_count: likeCounts[postId] || 0, comments_count: commentCounts[postId] || 0 } : p));
  }, []);

  // Fetch posts and attachments
  const loadPosts = useCallback(async (currentPage, reset = false) => {
    // Prevent duplicate requests
    if (loadPostsInProgressRef.current) {
      console.warn('loadPosts: Request already in progress, skipping duplicate');
      return;
    }
    if (!reset && !hasMoreRef.current) return;
    
    loadPostsInProgressRef.current = true;
    setLoading(true);
    
    const filters = filtersRef.current;
    const { data, error, originalDataLength } = await fetchPosts(
      currentPage, 
      filters.selectedHacks, 
      filters.selectedCategories, 
      filters.selectedBiomarkers, 
      filters.selectedBiomarkerCategories, 
      filters.activeFilter
    );
    
    if (error) {
      const is406Error = error.status === 406 || error.message?.includes('406') || error.code === '406';
      if (is406Error) {
        console.error('🚨 406 ERROR in loadPosts:', {
          error: sanitizeErrorForLog(error),
          page: currentPage,
          activeFilter: filtersRef.current.activeFilter,
          timestamp: new Date().toISOString()
        });
      } else {
        console.error('Error fetching posts:', sanitizeErrorForLog(error));
      }
      setLoading(false);
      loadPostsInProgressRef.current = false;
      return;
    }
    
    // Use originalDataLength (before client-side filtering) to determine if there are more posts
    // This ensures we continue fetching even if client-side filtering reduces the count
    // Only set hasMore to false if the database returned fewer than PAGE_SIZE posts
    if (originalDataLength < PAGE_SIZE) {
      setHasMore(false);
    }
    
    // Handle case where all posts were filtered out
    // If database still has more posts (originalDataLength === PAGE_SIZE), 
    // we should continue fetching (scroll handler will trigger next page)
    if (!data || data.length === 0) {
      if (originalDataLength === PAGE_SIZE) {
        // If we filtered out everything but there are likely more posts in DB,
        // automatically try the next page immediately instead of waiting for scroll
        console.log(`Page ${currentPage} filtered to empty, auto-loading next page`);
        loadPostsInProgressRef.current = false; // Reset lock to allow next fetch
        // Don't toggle loading state to prevent spinner flickering
        setPage(p => p + 1); // Trigger next page via useEffect
        return;
      }
      
      setLoading(false);
      loadPostsInProgressRef.current = false;
      return;
    }
    
    const postIds = data.map((p) => p.id);
    let postsWithBiomarkers = data;
    let biomarkerData = null;
    
    // Fetch biomarker attachments separately and merge into post data
    if (postIds.length) {
      biomarkerData = await fetchBiomarkerAttachments(postIds, data);
      
      postsWithBiomarkers = data.map(post => {
        if (biomarkerData[post.id]) {
          const attachmentMap = new Map((post.post_attachments || []).map(att => [att.id, att]));
          
          Object.values(biomarkerData[post.id]).forEach(({ attachment, biomarkers }) => {
            const existingAttachment = attachmentMap.get(attachment.id);
            if (!existingAttachment) {
              attachmentMap.set(attachment.id, {
                ...attachment,
                post_attachment_biomarkers: biomarkers.map(bm => ({
                  biomarker_id: bm.id,
                  biomarkers: bm
                }))
              });
            } else {
              attachmentMap.set(attachment.id, {
                ...existingAttachment,
                post_attachment_biomarkers: biomarkers.map(bm => ({
                  biomarker_id: bm.id,
                  biomarkers: bm
                }))
              });
            }
          });
          
          return {
            ...post,
            post_attachments: Array.from(attachmentMap.values())
          };
        }
        return post;
      });
    }
    
    // Batch-fetch original posts for reposts (so PostCard doesn't fetch per repost)
    const sharedFromIds = [...new Set(postsWithBiomarkers.map((p) => p.shared_from).filter(Boolean))];
    const idsToFetch = sharedFromIds.filter((id) => !originalPostsMapRef.current[id]);

    const { data: { session } } = await supabase.auth.getSession();
    const currentUserId = session?.user?.id;
    const authorIds = [...new Set(postsWithBiomarkers.map((p) => p.user_id).filter(Boolean))];

    const [batchOriginals, likesData, followStatusData] = await Promise.all([
      idsToFetch.length > 0 ? fetchOriginalPostsBatch(idsToFetch) : Promise.resolve([]),
      postIds.length > 0 ? fetchLikesAndCommentsForPosts(postIds) : Promise.resolve({ likeCounts: {}, likedMap: {}, commentCounts: {}, repostedMap: {} }),
      fetchFollowStatusesForAuthors(currentUserId, authorIds),
    ]);

    if (batchOriginals.length > 0) {
      // Fetch biomarkers for all originals (don't rely on batch response having biomarker rows)
      const originalIds = batchOriginals.filter((p) => p && p.id).map((p) => p.id);
      let biomarkerDataByOriginal = {};
      if (originalIds.length > 0) {
        biomarkerDataByOriginal = await fetchBiomarkerAttachments(originalIds, batchOriginals);
      }
      setOriginalPostsMap((prev) => {
        const next = { ...prev };
        batchOriginals.forEach((p) => {
          if (!p || !p.id) return;
          let merged = p;
          const biomarkerData = biomarkerDataByOriginal[p.id];
          if (biomarkerData && Object.keys(biomarkerData).length > 0) {
            const attachmentMap = new Map((p.post_attachments || []).map((att) => [att.id, { ...att }]));
            Object.values(biomarkerData).forEach(({ attachment, biomarkers }) => {
              const enriched = {
                post_attachment_biomarkers: (biomarkers || []).map((bm) => ({
                  biomarker_id: bm.id,
                  biomarkers: bm
                }))
              };
              const existing = attachmentMap.get(attachment.id);
              if (existing) {
                Object.assign(existing, enriched);
                attachmentMap.set(attachment.id, existing);
              } else {
                // Batch may not have returned this attachment row (e.g. RLS); add it from biomarker fetch
                attachmentMap.set(attachment.id, {
                  id: attachment.id,
                  post_id: attachment.post_id,
                  type: attachment.type,
                  sort_order: attachment.sort_order ?? 0,
                  ...enriched
                });
              }
            });
            const sorted = Array.from(attachmentMap.values()).sort(
              (a, b) => (a.sort_order ?? 0) - (b.sort_order ?? 0)
            );
            merged = { ...p, post_attachments: sorted };
          }
          next[p.id] = merged;
        });
        return next;
      });
    }

    if (reset) {
      setPosts(postsWithBiomarkers);
    } else {
      setPosts((prev) => {
        const existingIds = new Set(prev.map((p) => p.id));
        const newPosts = postsWithBiomarkers.filter((p) => !existingIds.has(p.id));
        return [...prev, ...newPosts];
      });
    }

    if (postIds.length && likesData) {
      const { likeCounts, likedMap, commentCounts, repostedMap } = likesData;
      setLikesInfo((prev) => {
        const updated = { ...prev };
        postIds.forEach((pid) => {
          updated[pid] = {
            count: likeCounts[pid] || 0,
            liked: !!likedMap[pid],
            likeId: likedMap[pid] || null,
            comments: commentCounts[pid] || 0,
            reposted: !!repostedMap[pid],
            repostId: repostedMap[pid] || null,
          };
        });
        return updated;
      });
    }

    if (Object.keys(followStatusData).length > 0) {
      setFollowStatusByAuthor((prev) => ({ ...prev, ...followStatusData }));
    }

    setLoading(false);
    loadPostsInProgressRef.current = false;
  }, []); // No dependencies - uses refs for filter values

  // Expose refresh and addNewPost methods to parent component
  useImperativeHandle(ref, () => ({
    refresh: () => {
      setPosts([]);
      setAttachments({});
      setPage(0);
      setHasMore(true);
      loadingRef.current = false;
      hasMoreRef.current = true;
      loadPostsInProgressRef.current = false;
      isInitialMountRef.current = false;
      loadPosts(0, true);
    },
    addNewPost: async (newPost) => {
      // Check if post should be shown based on current filters
      const filters = filtersRef.current;
      const filteredPosts = filterPosts(
        [newPost], 
        filters.selectedHacks, 
        filters.selectedCategories, 
        filters.selectedBiomarkers, 
        filters.selectedBiomarkerCategories
      );
      
      // Only add the post if it passes the filters
      if (filteredPosts.length === 0) {
        console.log('New post filtered out by current filters, not adding to feed');
        return;
      }
      
      // Check if we should show the post based on the active filter
      // For 'following' filter, check if user is viewing following feed
      if (filters.activeFilter === 'following') {
        // Get current user to check if this is their own post
        const { data: { session } } = await supabase.auth.getSession();
        const currentUserId = session?.user?.id;
        
        // If viewing following feed and this is user's own post, add it
        // (user implicitly "follows" themselves in the UI sense)
        // Or we could skip it - for now, let's add it
        if (newPost.user_id !== currentUserId) {
          // Check if user follows the post author
          const { data: followsData } = await supabase
            .from('follows')
            .select('followee_id')
            .eq('follower_id', currentUserId)
            .eq('followee_id', newPost.user_id)
            .maybeSingle();
          
          if (!followsData) {
            console.log('New post author not in following list, not adding to feed');
            return;
          }
        }
      }
      
      // Fetch biomarker attachments and merge into post data before adding to feed
      let postToAdd = newPost;
      if (newPost.post_attachments && newPost.post_attachments.length > 0) {
        const hasBiomarkerAttachments = newPost.post_attachments.some(att => att.type === 'biomarker');
        
        if (hasBiomarkerAttachments) {
          const biomarkerData = await fetchBiomarkerAttachments([newPost.id], [newPost]);
          
          if (biomarkerData[newPost.id]) {
            const attachmentMap = new Map((newPost.post_attachments || []).map(att => [att.id, att]));
            
            Object.values(biomarkerData[newPost.id]).forEach(({ attachment, biomarkers }) => {
              const existingAttachment = attachmentMap.get(attachment.id);
              if (!existingAttachment) {
                attachmentMap.set(attachment.id, {
                  ...attachment,
                  post_attachment_biomarkers: biomarkers.map(bm => ({
                    biomarker_id: bm.id,
                    biomarkers: bm
                  }))
                });
              } else {
                attachmentMap.set(attachment.id, {
                  ...existingAttachment,
                  post_attachment_biomarkers: biomarkers.map(bm => ({
                    biomarker_id: bm.id,
                    biomarkers: bm
                  }))
                });
              }
            });
            
            postToAdd = {
              ...newPost,
              post_attachments: Array.from(attachmentMap.values())
            };
          }
        }
      }
      
      // Add the new post to the beginning of the feed
      setPosts((prev) => [postToAdd, ...prev]);
      
      // Fetch likes and comments info for the new post
      const { likeCounts, likedMap, commentCounts, repostedMap } = await fetchLikesAndCommentsForPosts([newPost.id]);
      setLikesInfo((prev) => ({
        ...prev,
        [newPost.id]: {
          count: likeCounts[newPost.id] || 0,
          liked: !!likedMap[newPost.id],
          likeId: likedMap[newPost.id] || null,
          comments: commentCounts[newPost.id] || 0,
          reposted: !!repostedMap[newPost.id],
          repostId: repostedMap[newPost.id] || null,
        }
      }));
    }
  }), [loadPosts]);

  // Initial load on mount
  useEffect(() => {
    if (isInitialMountRef.current) {
      isInitialMountRef.current = false;
      loadPosts(0, true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Only run once on mount

  // Reset and fetch when filters change (skip on initial mount)
  useEffect(() => {
    if (isInitialMountRef.current) return;
    
    setPosts([]);
    setAttachments({});
    setPage(0);
    setHasMore(true);
    loadingRef.current = false;
    hasMoreRef.current = true;
    loadPostsInProgressRef.current = false; // Reset in-progress flag when filters change
    // Immediately fetch with reset flag
    loadPosts(0, true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedHacks, selectedCategories, selectedBiomarkers, selectedBiomarkerCategories, activeFilter]); // Removed loadPosts from deps

  // Fetch posts when page changes (for infinite scroll)
  useEffect(() => {
    if (page > 0) {
      loadPosts(page, false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page]); // Removed loadPosts from deps

  // Optimized scroll handler - use refs to avoid re-attaching on every state change
  useEffect(() => {
    if (!loader.current) return;
    const handleScroll = () => {
      if (
        loader.current &&
        !loadingRef.current &&
        !loadPostsInProgressRef.current &&
        hasMoreRef.current
      ) {
        const loaderRect = loader.current.getBoundingClientRect();
        const threshold = 500;
        if (loaderRect.top < window.innerHeight + threshold) {
          setPage((p) => p + 1);
        }
      }
    };
    let ticking = false;
    const throttledHandleScroll = () => {
      if (!ticking) {
        window.requestAnimationFrame(() => {
          handleScroll();
          ticking = false;
        });
        ticking = true;
      }
    };
    handleScroll();
    window.addEventListener('scroll', throttledHandleScroll, { passive: true });
    return () => window.removeEventListener('scroll', throttledHandleScroll);
  }, []);

  // Re-check scroll position after posts are loaded to trigger next page if needed
  useEffect(() => {
    if (!loading && !loadPostsInProgressRef.current && posts.length > 0 && hasMore) {
      // Small delay to ensure DOM is updated
      const timer = setTimeout(() => {
        if (loader.current && !loadingRef.current && !loadPostsInProgressRef.current && hasMoreRef.current) {
          const loaderRect = loader.current.getBoundingClientRect();
          const threshold = 500;
          if (loaderRect.top < window.innerHeight + threshold) {
            setPage((p) => p + 1);
          }
        }
      }, 100);
      return () => clearTimeout(timer);
    }
  }, [loading, posts.length, hasMore]);

  // Real-time updates for likes/comments
  useEffect(() => {
    const subscription = supabase
      .channel('public:posts')
      .on('postgres_changes', { event: '*', schema: 'public', table: 'posts' }, (payload) => {
        setPosts((prev) =>
          prev.map((post) =>
            post.id === payload.new.id
              ? { ...post, likes_count: payload.new.likes_count, comments_count: payload.new.comments_count }
              : post
          )
        );
      })
      .subscribe();
    return () => {
      supabase.removeChannel(subscription);
    };
  }, []);

  // Memoize the callback handler to prevent recreating it for every post on every render
  const handleCountsChanged = useCallback((postId, data) => {
    if (data && data.updatedPost) {
      // This is a post update (e.g., edit), update the post in the feed
      setPosts(prev => prev.map(p => p.id === data.updatedPost.id ? { ...p, ...data.updatedPost } : p));
      // Refresh counts to get latest state after edit
      refreshCountsForPost(postId);
    } else if (data && data.id) {
      // This is a new repost post, add it to the beginning of the feed
      setPosts(prev => [data, ...prev]);
      // Refresh counts for the original post (repost count changed)
      refreshCountsForPost(postId);
    } else if (data && data.removeRepostId) {
      // This is a repost removal, remove the repost post from the feed
      setPosts(prev => prev.filter(p => p.id !== data.removeRepostId));
      // Refresh counts for the original post (repost count changed)
      refreshCountsForPost(postId);
    } else if (data && data.deletedPostId) {
      // This is a post deletion, remove the deleted post from the feed
      setPosts(prev => prev.filter(p => p.id !== data.deletedPostId));
    } else if (data) {
      // This is just a count update - but real-time subscription handles most of these
      // Only refresh if explicitly needed (e.g., after share action that doesn't trigger real-time)
      // For likes/comments, real-time subscription already handles it, so skip duplicate fetch
    } else {
      // Empty call - don't do anything to avoid unnecessary fetches
      // Real-time subscription handles count updates automatically
    }
  }, [refreshCountsForPost]);

  // Memoize processed posts to avoid recalculating attachedProtocols on every render
  const processedPosts = useMemo(() => {
    return posts.map((post) => {
      // Compute attachedProtocols once per post
      const attachedProtocols = (post.post_attachments || [])
        .filter(att => att.type === 'protocols')
        .flatMap(att => (att.post_attachment_protocols || [])
          .map(pap => pap.protocols)
        );
      
      return {
        ...post,
        level: post.user_profiles?.user_level,
        likes_count: likesInfo[post.id]?.count,
        comments_count: likesInfo[post.id]?.comments,
        attachedProtocols
      };
    });
  }, [posts, likesInfo]);

  return (
    <div className="post-feed">
      <div className="home-feed-grid">
        {processedPosts.map((post) => (
          <PostCard
            key={post.id}
            post={post}
            likesInfo={likesInfo[post.id]}
            followStatusForAuthor={followStatusByAuthor[post.user_id]}
            onFollowStateChange={(followeeId, status) =>
              setFollowStatusByAuthor((prev) => ({ ...prev, [followeeId]: status }))
            }
            onCountsChanged={(data) => handleCountsChanged(post.id, data)}
            userVotes={userVotes}
            handleVote={handleVoteAction}
            originalPostBySharedFrom={originalPostsMap}
          />
        ))}
      </div>
      <div className="post-feed-loader-wrapper">
        <div ref={loader} style={{ height: 0 }} />
        {loading && (
          <div className="post-feed-loading">
            <div className="post-feed-loading-spinner"></div>
          </div>
        )}
        {!hasMore && !loading && (
          <div className="end-feed">
            {posts.length === 0 ? (
              <>
                <span>No posts</span>
                <span>Adjust filters</span>
              </>
            ) : (
              <span>No more posts</span>
            )}
          </div>
        )}
      </div>
    </div>
  );
});

PostFeed.displayName = 'PostFeed';

export default PostFeed; 